import { create } from "zustand";
import { getToday } from "@/lib/helpers";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

// ---------------------- Variables

const enableStore = import.meta.env.VITE_CURRENT_MODE !== "production";

// ---------------------- Interfaces

interface DayBookings {
  [day: number]: string[];
}

interface BookingsCurrentState {
  // --------- State

  expandedDays: number[];
  expandedBookings: DayBookings;
  registeredBookings: DayBookings;
  refetchFunction: (() => void) | null;

  // --------- Actions

  setExpandedDay: (day: number) => void;
  setAllDaysExpanded: () => void;
  setNoDaysExpanded: () => void;
  isDayExpanded: (day: number) => boolean;

  setExpandedBooking: (day: number, id: string) => void;
  isBookingExpanded: (day: number, id: string) => boolean;

  registerBooking: (day: number, id: string) => void;

  initialize: () => void;
  reset: () => void;

  setRefetchFunction: (fn: () => void) => void;
  triggerRefetch: () => void;
}

// ---------------------- Store

export const useBookingsCurrentStore = create<BookingsCurrentState>()(
  devtools(
    immer((set, get) => ({
      // --------- State

      expandedDays: [],
      expandedBookings: {},
      registeredBookings: {},
      refetchFunction: null,

      // --------- Actions

      setExpandedDay: (day: number) =>
        set(
          (state) => {
            if (state.expandedDays.includes(day)) {
              state.expandedDays = state.expandedDays.filter((item) => item !== day);
              delete state.expandedBookings[day];
            } else {
              state.expandedDays.push(day);
            }
          },
          false,
          "bookings-current/setExpandedDay",
        ),

      isDayExpanded: (day: number) => {
        const state = get();
        return state.expandedDays.includes(day);
      },

      setAllDaysExpanded: () =>
        set(
          (state) => {
            state.expandedDays = [0, 1, 2, 3, 4, 5, 6];
          },
          false,
          "bookings-current/setAllDaysExpanded",
        ),

      setNoDaysExpanded: () =>
        set(
          (state) => {
            for (let day = 0; day <= 6; day++) {
              state.expandedBookings[day] = [];
            }

            state.expandedDays = [];
          },
          false,
          "bookings-current/setNoDaysExpanded",
        ),

      setExpandedBooking: (day: number, id: string) =>
        set(
          (state) => {
            if (!state.expandedBookings[day]) {
              state.expandedBookings[day] = [];
            }

            if (state.expandedBookings[day].includes(id)) {
              state.expandedBookings[day] = state.expandedBookings[day].filter(
                (item) => item !== id,
              );

              if (state.expandedBookings[day].length === 0) {
                delete state.expandedBookings[day];
              }
            } else {
              state.expandedBookings[day].push(id);
            }
          },
          false,
          "bookings-current/setExpandedBooking",
        ),

      isBookingExpanded: (day: number, id: string) => {
        const state = get();
        return state.expandedBookings[day]?.includes(id) ?? false;
      },

      // this doesn't seem to do anything now but we need this registration in place
      // in case we ever decide to open/close bookings automatically when we open/close days
      // so even though not using now, leave in for future
      registerBooking: (day: number, id: string) =>
        set(
          (state) => {
            if (!state.registeredBookings[day]) {
              state.registeredBookings[day] = [];
            }

            if (state.registeredBookings[day].includes(id)) {
              return;
            } else {
              state.registeredBookings[day].push(id);
            }
          },
          false,
          "bookings-current/registerBooking",
        ),

      initialize: () =>
        set(
          (state) => {
            state.expandedDays = [getToday()];
          },
          false,
          "bookings-current/initialize",
        ),

      reset: () =>
        set(
          (state) => {
            state.expandedDays = [];
            state.expandedBookings = {};
          },
          false,
          "bookings-current/reset",
        ),

      setRefetchFunction: (fn: () => void) =>
        set(
          (state) => {
            state.refetchFunction = fn;
          },
          false,
          "bookings-current/setRefetchFunction",
        ),

      triggerRefetch: () =>
        set(
          (state) => {
            state.refetchFunction?.();
          },
          false,
          "bookings-current/triggerRefetch",
        ),
    })),
    {
      name: "BookingsCurrentStore",
      enabled: enableStore,
      store: "BookingsCurrentStore",
    },
  ),
);
